import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import {  Observable } from 'rxjs';
import { MessageDialogComponent } from 'src/app/components/controls/message-dialog/message-dialog.component';
import { ReadingLevel } from 'src/app/core/constants/reading-level';
import { SetPageHeaderTitle } from 'src/app/core/data-management/actions/projects.action';
import { BasicResponse } from 'src/app/core/interfaces/basic-response';
import { VisitStatus } from 'src/app/_models/ImagingProject/IF/incidental-findings-config-model';
import { ImagingProjectService } from 'src/app/_services';
import { ToastService } from 'src/app/_services/internal/toast.service';
import { ReadingConfigFlexibleService } from 'src/app/_services/reading-config-flexible.service';
import { ReadingGenantService } from 'src/app/_services/reading-genant.service';
import { ReadingFormScoring, ReadingFormScoringComponent } from '../../../reading-form-scoring.component';

@Component({
  selector: 'app-genant-level1-reading-form',
  templateUrl: './genant-level1-reading-form.component.html',
  styleUrls: ['./genant-level1-reading-form.component.css']
})
export class GenantLevel1ReadingFormComponent extends ReadingFormScoringComponent implements OnInit, ReadingFormScoring {

  currentReading;
  readingForm: FormGroup = this.fb.group({});
  visitsArrayForm: FormArray = this.fb.array([]);
  selectedVisit = 0;
  previousTimePoint;

  constructor(
    private store: Store,
    private readingGenantService: ReadingGenantService,
    private readingConfigService: ReadingConfigFlexibleService,
    private imagingProjectService: ImagingProjectService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    this.store.dispatch(new SetPageHeaderTitle('Genant'));
    this.initOnCurrentReading();
    this.readingSeriesInitiated.subscribe(resp => {
      if (resp)
        this.chooseActiveVisitByStatus();
    })
  }

  initOnCurrentReading(): void {
    this.currentReadingSubject.subscribe(currentReading => {
      if (currentReading.readingLevel === ReadingLevel.LONGITUDINAL) {
        this.switchSubmitBtnDisabledSubject.next(false);
      }
      this.readingForm = new FormGroup({});
      this.visitsArrayForm = new FormArray([]);
      this.currentReading = currentReading;
      this.getPreviousTimePoint();
      this.initReadingForm();
    });
  }

  initReadingForm(): void {
    this.currentReading.visits = this.currentReading.visits.sort((a, b) => a.visitOrder - b.visitOrder);

    this.currentReading.visits.forEach((v, visitIndex) => {
      //create visit form
      const visitFormControl = new FormGroup({
        id: new FormControl(v.id),
        visitConfigId: new FormControl(v.visitConfigId),
        status: new FormControl(v.status),
        visitOrder: new FormControl(v.visitOrder),
        disagreement: new FormControl(v.disagreement),
        score: new FormGroup({
          id: new FormControl(v.score.id),
          t4: new FormControl(v.score.t4 == null ? 'GRADE_0' : v.score.t4, Validators.required),
          t5: new FormControl(v.score.t5 == null ? 'GRADE_0' : v.score.t5, Validators.required),
          t6: new FormControl(v.score.t6 == null ? 'GRADE_0' : v.score.t6, Validators.required),
          t7: new FormControl(v.score.t7 == null ? 'GRADE_0' : v.score.t7, Validators.required),
          t8: new FormControl(v.score.t8 == null ? 'GRADE_0' : v.score.t8, Validators.required),
          t9: new FormControl(v.score.t9 == null ? 'GRADE_0' : v.score.t9, Validators.required),
          t10: new FormControl(v.score.t10 == null ? 'GRADE_0' : v.score.t10, Validators.required),
          t11: new FormControl(v.score.t11 == null ? 'GRADE_0' : v.score.t11, Validators.required),
          t12: new FormControl(v.score.t12 == null ? 'GRADE_0' : v.score.t12, Validators.required),
          l1: new FormControl(v.score.l1 == null ? 'GRADE_0' : v.score.l1, Validators.required),
          l2: new FormControl(v.score.l2 == null ? 'GRADE_0' : v.score.l2, Validators.required),
          l3: new FormControl(v.score.l3 == null ? 'GRADE_0' : v.score.l3, Validators.required),
          l4: new FormControl(v.score.l4 == null ? 'GRADE_0' : v.score.l4, Validators.required),
          comment: new FormControl(v.score.comment),
        }),
      });

      (<FormArray>this.visitsArrayForm).push(visitFormControl);
      visitFormControl.valueChanges.subscribe(() => {
        this.checkValidity(visitFormControl);
      });
    });
    this.readingForm.addControl('visits', this.visitsArrayForm);
    this.checkValidity(this.readingForm);
  }

  getPreviousTimePoint() {
    this.previousTimePoint = null;
    if (this.currentReading && this.currentReading.visits.length > 1) {
      const currentVisit = this.currentReading.visits.find(v => v.status === VisitStatus.IN_PROGRESS && v.visitOrder !== 1);
      if (currentVisit !== undefined) {
        const uploadedVisits = this.currentReading.visits.filter(v => v.noUploads === false).sort((a, b) => a.visitOrder - b.visitOrder);
        const previousVisitIndex = uploadedVisits.findIndex(visit => visit.id === currentVisit.id) - 1;
        if (uploadedVisits[previousVisitIndex].disagreement === true) {
          this.previousTimePoint = this.currentReading.adjudicationReading.visits.find(v => v.visitConfigId === uploadedVisits[previousVisitIndex].visitConfigId).score;
        } else
          this.previousTimePoint = uploadedVisits[previousVisitIndex].score;
      }
    }
  }

  getExactPreviousScore(visitStatus, fieldScore) {
    if (visitStatus !== VisitStatus.IN_PROGRESS)
      return 'NA'
    return this.previousTimePoint === null ? '' : this.toTitleCase(this.previousTimePoint[fieldScore]);
  }

  // current visit score must be not less then prior visit, otherwice comment required
  isTrustedScore(current: string, previous: string) {
    const score = (t) => (t === 'GRADE_0' ? 0 : t === 'GRADE_1' ? 1 : t === 'GRADE_2' ? 2 : t === 'GRADE_3' ? 3 : -1);
    return score(current) >= score(previous);
  }

  isScoreValid(scoreName) {
    const controls = this.getVisitControls(this.selectedVisit);
    const a = controls ? (<FormGroup>controls.score).controls[scoreName].value : null;

    return !this.previousTimePoint || this.isTrustedScore(a, this.previousTimePoint[scoreName]);
  }

  isCommentOptional(currentScore) {
    
    return !this.previousTimePoint ||
        (this.isTrustedScore(currentScore.score.l1, this.previousTimePoint.l1)
        && this.isTrustedScore(currentScore.score.l2, this.previousTimePoint.l2)
        && this.isTrustedScore(currentScore.score.l3, this.previousTimePoint.l3)
        && this.isTrustedScore(currentScore.score.l4, this.previousTimePoint.l4)
        && this.isTrustedScore(currentScore.score.t4, this.previousTimePoint.t4)
        && this.isTrustedScore(currentScore.score.t5, this.previousTimePoint.t5)
        && this.isTrustedScore(currentScore.score.t6, this.previousTimePoint.t6)
        && this.isTrustedScore(currentScore.score.t7, this.previousTimePoint.t7)
        && this.isTrustedScore(currentScore.score.t8, this.previousTimePoint.t8)
        && this.isTrustedScore(currentScore.score.t9, this.previousTimePoint.t9)
        && this.isTrustedScore(currentScore.score.t10, this.previousTimePoint.t10)
        && this.isTrustedScore(currentScore.score.t11, this.previousTimePoint.t11)
        && this.isTrustedScore(currentScore.score.t12, this.previousTimePoint.t12));
  }

  isCommentValid() {
    const currentScore = this.buildDatasObject(this.selectedVisit, VisitStatus.IN_PROGRESS) as any;
    return currentScore && (currentScore.score.comment || this.isCommentOptional(currentScore));
  }

  onChangeValue(event) {
    this.checkValidity(this.readingForm);
  }

  openGuidlineDialogBox(){
    const  content = '<img width="100%" src="assets/images/genant-scoring-info.png"/>';
    this.dialog.open(MessageDialogComponent, {
      data: {message: content, html: true}
    });
  }

  toTitleCase(str) {
    return str.toLowerCase().split('_').map(function (word) {
      return (word.charAt(0).toUpperCase() + word.slice(1));
    }).join(' ');
  }

  chooseActiveVisitByStatus() {
    const activeVisitIndex = this.currentReading.visits.findIndex(visit => visit.status === VisitStatus.IN_PROGRESS);
    if (activeVisitIndex !== -1)
      this.onChangeActivatedVisit(activeVisitIndex);
  }

  onChangeActivatedVisit(index) {
    this.selectedVisit = index;
  }

  private checkValidity(formControl) {
    const commentValid = this.isCommentValid();
    this.switchSubmitBtnDisabledSubject.next(formControl.invalid || !commentValid);
  }

  getEndpointName(): string {
    return 'GENANT';
  }

  loadReadings(studyId: number, readerId: number): Observable<BasicResponse<any>> {
    return this.readingGenantService.getAvailableLevelOneReadings(studyId, readerId);
  }

  startReading(studyId: number, readingId: number): Observable<BasicResponse<any>> {
    return this.readingGenantService.startReading(studyId, readingId);
  }

  updateReading(studyId: number, readingId: number, data: { spentSeconds: number; }, justSaveTime?: boolean): Observable<BasicResponse<any>> {
    this.currentReading.timeSpent = data.spentSeconds;
    const readingData = {
      spentSeconds: data.spentSeconds,
    }
    const model = this.buildDatasObject(this.selectedVisit, VisitStatus.DONE);
    const visit = this.currentReading.visits[this.selectedVisit];
    visit.score = (model as any).score;
    this.updateReadingNavigator(this.currentReading);
    if (justSaveTime)
      return this.readingGenantService.updateReading(studyId, readingId, readingData);
    return this.skipReading(studyId, { spentSeconds: data.spentSeconds });
  }

  submitReading(studyId: number, data: { spentSeconds: number; }): Observable<BasicResponse<any>> {
    this.currentReading.timeSpent = data.spentSeconds;
    const model = this.buildDatasObject(this.selectedVisit, VisitStatus.DONE);
    const visit = this.currentReading.visits[this.selectedVisit];
    return this.readingGenantService.lockTimepoint(studyId, visit.id, model);
  }

  skipReading(studyId: number, data: { spentSeconds: number; }): Observable<BasicResponse<any>> {
    const model = this.buildDatasObject(this.selectedVisit, VisitStatus.IN_PROGRESS);
    const visit = this.currentReading.visits[this.selectedVisit];
    return this.readingGenantService.lockTimepoint(studyId, visit.id, model);
  }

  updateReadingNavigator(reading: any) {
    if (!this.readingListUpdatedSubject.closed)
      this.readingListUpdatedSubject.next(reading);
  }

  buildDatasObject(visitIndex: number, status: string): Object {
    const data = Object();
    const controls = this.getVisitControls(visitIndex);
    data.id = controls.id.value;
    data.visitConfigId = controls.visitConfigId.value;
    data.status = status;
    data.disagreement = controls.disagreement.value;
    data.visitOrder = controls.visitOrder.value;
    data.score = {
      id: (<FormGroup>controls.score).controls.id.value,
      t4: (<FormGroup>controls.score).controls.t4.value,
      t5: (<FormGroup>controls.score).controls.t5.value,
      t6: (<FormGroup>controls.score).controls.t6.value,
      t7: (<FormGroup>controls.score).controls.t7.value,
      t8: (<FormGroup>controls.score).controls.t8.value,
      t9: (<FormGroup>controls.score).controls.t9.value,
      t10: (<FormGroup>controls.score).controls.t10.value,
      t11: (<FormGroup>controls.score).controls.t11.value,
      t12: (<FormGroup>controls.score).controls.t12.value,
      l1: (<FormGroup>controls.score).controls.l1.value,
      l2: (<FormGroup>controls.score).controls.l2.value,
      l3: (<FormGroup>controls.score).controls.l3.value,
      l4: (<FormGroup>controls.score).controls.l4.value,
      comment: (<FormGroup>controls.score).controls.comment.value,
    }
    return data;
  }

  getVisitControls(visitIndex: number) {
    return (<FormGroup>(<FormGroup>(<FormArray>this.readingForm.controls.visits).controls[visitIndex]))?.controls;
  }

  clearForm(): void {
  }

}
